import {
   Box,
   FormControl,
   FormErrorMessage,
   FormLabel,
   Input,
   InputGroup,
   InputRightElement,
   List,
   ListItem,
   Spinner,
   Text
} from '@chakra-ui/react';
import { VehicleResponseDTO } from 'models/VehicleResponseDTO';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { getVehicles } from 'services/vehicles';
import { useDebounce } from 'utils/useDebounce';

export interface VehicleAutocompleteProps {
  name: string;
  label: string;
  required?: boolean;
  isDisable?: boolean;
  err?: { message?: string }; 
}

export const VehicleAutocomplete: React.FC<VehicleAutocompleteProps> = ({
  name,
  label,
  required = false,
  isDisable = false,
  err
}) => {
  const { control } = useFormContext();

  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState<VehicleResponseDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showOptions, setShowOptions] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);

  // <-- MUDANÇA: Lógica de Debounce Totalmente Refatorada
  // 1. Criamos a lógica de busca dentro de um useCallback para
  //    garantir que a função seja estável entre re-renderizações.
  const searchVehiclesCallback = useCallback((term: string) => {
    if (term && term.length > 1) {
      setIsLoading(true);
      getVehicles(term).then((data) => {
        setOptions(data);
        setIsLoading(false);
        setShowOptions(data.length > 0);
      });
    } else {
      setOptions([]);
      setShowOptions(false);
      setIsLoading(false);
    }
  }, []); 

  // 2. Passamos nossa função estável para o useDebounce.
  //    Isso cria uma NOVA função (debouncedSearchHandler) que só
  //    executará a 'searchVehiclesCallback' 400ms após a última chamada.
  const debouncedSearchHandler = useDebounce(searchVehiclesCallback, 200);


  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setShowOptions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [wrapperRef]);

  return (
    <Controller
      name={name}
      control={control}
      defaultValue=""
      render={({ field: { onChange, onBlur, value } }) => {
        useEffect(() => {
          if (value !== undefined && searchTerm !== value) {
            setSearchTerm(value);
          }
        }, [value, searchTerm]); 

        const handleInputChange = (
          event: React.ChangeEvent<HTMLInputElement>
        ) => {
          const text = event.target.value;
          setSearchTerm(text);
          onChange(text);
          debouncedSearchHandler(text);
        };

        const handleOptionClick = (vehicle: VehicleResponseDTO) => {
          const formattedValue = `${vehicle.marca}/${vehicle.modelo}`;
          setSearchTerm(formattedValue);
          onChange(formattedValue);
          setOptions([]);
          setShowOptions(false);
        };

        const handleBlur = () => {
          onBlur(); 
          setTimeout(() => setShowOptions(false), 150);
        };

        return (
          <FormControl
            ref={wrapperRef}
            isInvalid={!!err}
            position="relative"
            isRequired={required}
          >
            {label && <FormLabel htmlFor={name}>{label}</FormLabel>}
            <InputGroup>
              <Input
                id={name}
                value={searchTerm}
                onChange={handleInputChange}
                onBlur={handleBlur}
                onFocus={() => {
                  if (options.length > 0) setShowOptions(true);
                }}
                
                placeholder="Digite para buscar"
                autoComplete="off"
                isDisabled={isDisable}
              />
              {isLoading && (
                <InputRightElement>
                  <Spinner size="sm" color="gray.400" />
                </InputRightElement>
              )}
            </InputGroup>
            
            {/* Lista de Opções */}
            {showOptions && options.length > 0 && (
              <Box
                position="absolute"
                top="100%"
                width="100%"
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="md"
                bg="white"
                zIndex="popover"
                maxHeight="250px"
                overflowY="auto"
                boxShadow="lg"
                mt={1}
              >
                <List spacing={1} p={1}>
                  {options.map((vehicle) => (
                    <ListItem
                      key={vehicle.guid}
                      p={2}
                      _hover={{ bg: 'gray.100', cursor: 'pointer' }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleOptionClick(vehicle);
                      }}
                    >
                      <Text fontWeight="bold" as="span">{vehicle.marca}</Text>
                      <Text as="span"> / {vehicle.modelo}</Text>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
            
            <FormErrorMessage>{err?.message}</FormErrorMessage>
          </FormControl>
        );
      }}
    />
  );
};

export default VehicleAutocomplete;